import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import PublicHeader from '../components/PublicHeader';
import PageHeader from '../components/PageHeader';
import UpcomingSection from '../components/UpcomingSection';
import SitePlanSection from '../components/SitePlanSection';
import Footer from '../components/Footer';
import ProjectDetailPage from './ProjectDetailPage';
import ProjectResearchPage from './ProjectResearchPage';
import { useSitePlans } from '../hooks/useSitePlans';
import { SitePlanItem, UpcomingProject } from '../types';

interface SitePlansPageProps {
  /**
   * Hide the "Project Research" entry-point button without removing it from
   * the tree. Used by share-link recipients whose share `Module` is
   * `SITE_PLANS` — they should not be able to cross into Project Research.
   * We `display: none` rather than unmounting so the surrounding button code
   * stays in place for agent-side flows that may resurrect it.
   */
  hideProjectResearchButton?: boolean;
}

export const SitePlansPage: React.FC<SitePlansPageProps> = ({
  hideProjectResearchButton = false,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SitePlanItem | UpcomingProject | null>(null);
  const [showResearch, setShowResearch] = useState(false);
  const { upcoming, sitePlans, loading, error } = useSitePlans();

  const filteredUpcoming = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return upcoming;
    return upcoming.filter((p) => p.name.toLowerCase().includes(q));
  }, [search, upcoming]);

  const filteredPlans = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sitePlans;
    return sitePlans.filter((p) => p.projectName.toLowerCase().includes(q));
  }, [search, sitePlans]);

  if (showResearch) {
    return <ProjectResearchPage onBack={() => setShowResearch(false)} />;
  }

  if (selected) {
    return (
      <ProjectDetailPage
        projectId={selected.id}
        fromListItem={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicHeader />
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: hideProjectResearchButton ? 'none' : 'flex',
            justifyContent: 'flex-end',
            mb: 1.5,
          }}
        >
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => setShowResearch(true)}
            sx={{
              bgcolor: 'var(--color-accent)',
              '&:hover': { bgcolor: 'var(--color-accent)' },
            }}
          >
            Project Research
          </Button>
        </Box>
        <PageHeader search={search} onSearchChange={setSearch} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load site plans: {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <UpcomingSection projects={filteredUpcoming} onProjectClick={setSelected} />
            <SitePlanSection plans={filteredPlans} onPlanClick={setSelected} />
          </>
        )}

        <Footer />
      </Box>
    </Box>
  );
};

export default SitePlansPage;
